import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface TransactionTypeData {
  category: string;
  count: number;
  amount: number;
}

interface TransactionTypesChartProps {
  data: TransactionTypeData[];
  title?: string;
  height?: number;
  showBy?: 'count' | 'amount';
}

/**
 * Pie chart showing transaction distribution by category
 */
export function TransactionTypesChart({
  data,
  title = 'Transaction Types',
  height = 350,
  showBy = 'amount',
}: TransactionTypesChartProps) {
  const chartOptions = useMemo(() => {
    const categories = data.map((item) => item.category);
    const values =
      showBy === 'count'
        ? data.map((item) => item.count)
        : data.map((item) => item.amount);

    const colors = [
      '#3b82f6', // blue-500
      '#10b981', // green-500
      '#f59e0b', // amber-500
      '#ef4444', // red-500
      '#8b5cf6', // violet-500
      '#ec4899', // pink-500
      '#06b6d4', // cyan-500
      '#f97316', // orange-500
      '#84cc16', // lime-500
      '#6366f1', // indigo-500
    ];

    return {
      series: values,
      options: {
        chart: {
          type: 'pie' as const,
          height,
          toolbar: {
            show: true,
            tools: {
              download: true,
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        labels: categories,
        colors: colors.slice(0, categories.length),
        legend: {
          position: 'bottom' as const,
          horizontalAlign: 'center' as const,
          fontSize: '14px',
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val.toFixed(1)}%`,
          style: {
            fontSize: '12px',
            fontWeight: 600,
          },
        },
        tooltip: {
          y: {
            formatter: (value: number) =>
              showBy === 'count'
                ? `${value.toLocaleString()} transactions`
                : `${value.toLocaleString()} coins`,
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: true,
            donut: {
              labels: {
                show: false,
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 300,
              },
              legend: {
                position: 'bottom' as const,
                fontSize: '12px',
              },
            },
          },
        ],
      },
    };
  }, [data, height, showBy]);

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title}
          <span className="text-sm font-normal text-muted-foreground ml-2">
            (by {showBy === 'count' ? 'transaction count' : 'coin amount'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type="pie"
          height={height}
        />
      </CardContent>
    </Card>
  );
}
